import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Trash2 } from 'lucide-react';

const LABEL_OPTIONS = {
  phones: ['Mobile', 'Home', 'Office', 'Work', 'Personal', 'Main', 'Fax', 'Other'],
  personal_phones: ['Mobile', 'Home', 'Office', 'Work', 'Personal', 'Main', 'Fax', 'Other'],
  emails: ['Work', 'Personal', 'Home', 'Other'],
  personal_emails: ['Work', 'Personal', 'Home', 'Other'],
  websites: ['Website', 'Portfolio', 'Blog', 'Company', 'LinkedIn', 'Other'],
  personal_websites: ['Website', 'Portfolio', 'Blog', 'Personal', 'Other'],
  addresses: ['Work', 'Home', 'Office', 'Other'],
  personal_addresses: ['Home', 'Work', 'Other'],
  job_titles: ['Primary', 'Secondary', 'Consultant', 'Advisor', 'Other']
};

const MultiEntryField = ({ 
  field, // 'phones', 'emails', 'websites', 'addresses', 'job_titles'
  entries = [], 
  onChange, 
  icon: Icon,
  placeholder,
  inputType = 'text',
  isTextarea = false,
  label
}) => {
  const labelOptions = LABEL_OPTIONS[field] || ['Primary', 'Other'];
  
  const addEntry = () => {
    onChange([...entries, { label: labelOptions[0], value: '' }]);
  };
  
  const updateEntry = (index, key, value) => {
    const updated = entries.map((entry, i) => 
      i === index ? { ...entry, [key]: value } : entry
    );
    onChange(updated);
  };
  
  const removeEntry = (index) => {
    onChange(entries.filter((_, i) => i !== index));
  };
  
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">{label}</Label>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={addEntry}
          className="text-primary hover:text-primary/80 gap-1 h-8"
          data-testid={`add-${field}-btn`}
        >
          <Plus className="w-4 h-4" />
          Add
        </Button>
      </div>
      
      {entries.length === 0 && (
        <p className="text-sm text-muted-foreground italic">
          No {field.replace('_', ' ')} added yet. Click "Add" to add one.
        </p>
      )}
      
      <div className="space-y-2">
        {entries.map((entry, index) => (
          <div key={index} className="flex items-start gap-2">
            <Select
              value={entry.label}
              onValueChange={(value) => updateEntry(index, 'label', value)}
            >
              <SelectTrigger className="w-28 h-10" data-testid={`${field}-${index}-label`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {labelOptions.map((opt) => (
                  <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <div className="flex-1 relative">
              {Icon && (
                <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              )}
              {isTextarea ? (
                <Textarea
                  placeholder={placeholder}
                  value={entry.value}
                  onChange={(e) => updateEntry(index, 'value', e.target.value)}
                  className={Icon ? 'pl-10' : ''}
                  rows={2}
                  data-testid={`${field}-${index}-value`}
                />
              ) : (
                <Input
                  type={inputType}
                  placeholder={placeholder}
                  value={entry.value}
                  onChange={(e) => updateEntry(index, 'value', e.target.value)}
                  className={Icon ? 'pl-10 h-10' : 'h-10'}
                  data-testid={`${field}-${index}-value`}
                />
              )}
            </div>
            
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => removeEntry(index)}
              className="text-destructive hover:text-destructive hover:bg-destructive/10 h-10 w-10"
              data-testid={`remove-${field}-${index}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MultiEntryField;
